import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { MarketBadge } from '../components/UI'
import toast from 'react-hot-toast'

function useProducts() {
  const [products, setProducts] = useState([])
  useEffect(() => { api.getProducts().then(setProducts).catch(() => {}) }, [])
  return products
}
function useActiveMarkets() {
  const [markets, setMarkets] = useState([])
  useEffect(() => {
    api.getSettings().then(s => setMarkets(s.active_markets || ['SE','NL-BE','FI','DK','NO']))
      .catch(() => setMarkets(['SE','NL-BE','FI','DK','NO']))
  }, [])
  return markets
}

const MARKET_CURRENCY = { SE:'SEK', DK:'DKK', NO:'NOK', FI:'EUR', 'NL-BE':'EUR', UK:'GBP', US:'USD', AU:'AUD', CA:'CAD', GE:'EUR', FR:'EUR' }

export default function PricingAgent() {
  const products = useProducts()
  const activeMarkets = useActiveMarkets()
  const [productId, setProductId] = useState('')
  const [rates, setRates] = useState(null)
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState(null)
  const [applying, setApplying] = useState('')

  useEffect(() => { api.getRates().then(d => setRates(d.rates)).catch(() => {}) }, [])

  const product = products.find(p => String(p.product_id) === String(productId))
  const curFor = (m) => MARKET_CURRENCY[m] || 'EUR'
  const convertedFor = (m) => {
    if (!product?.selling_price_eur) return null
    const cur = curFor(m)
    if (cur === 'EUR') return Math.round(parseFloat(product.selling_price_eur))
    const rate = rates?.[cur]
    return rate ? Math.round(parseFloat(product.selling_price_eur) * rate) : null
  }

  async function run() {
    if (!productId) { toast.error('Pick a product'); return }
    setRunning(true); setResult(null)
    const markets = activeMarkets.map(m => ({ market: m, currency: curFor(m), converted: convertedFor(m) }))
    try {
      const { jobId } = await api.pricingAgentAnalyze({ productId: Number(productId), markets })
      if (!jobId) throw new Error('Could not start analysis')
      let tries = 0
      const poll = async () => {
        tries++
        try {
          const st = await api.pricingAgentStatus(jobId)
          if (!st.done) { if (tries > 90) { setRunning(false); toast.error('Analysis timed out'); return } setTimeout(poll, 4000); return }
          if (st.error) { toast.error(st.error); setRunning(false); return }
          if (!st.result) { toast.error('Could not produce a structured result — try again'); setResult(st) }
          else setResult(st)
          setRunning(false)
        } catch (err) { if (tries > 90) { setRunning(false); toast.error(err.message); return } setTimeout(poll, 4000) }
      }
      setTimeout(poll, 4000)
    } catch (err) { toast.error(err.message); setRunning(false) }
  }

  async function importModel(market, model) {
    if (!window.confirm(`Import to ${market}?\n\n${model.offer}\n\nThis sets the ${market} offer for this product (used in campaign names).`)) return
    setApplying(market + model.strategy)
    try {
      await api.pricingAgentApply({ productId: Number(productId), market, offer: model.offer })
      toast.success(`Imported to ${market}`)
    } catch (err) { toast.error(err.message) }
    setApplying('')
  }

  const r = result?.result
  const scoreColor = (s) => s >= 8 ? 'var(--accent3)' : s >= 6 ? '#F59E0B' : 'var(--text3)'

  return (
    <div style={{flex:1,overflow:'auto',padding:'24px 28px',maxWidth:1100}}>
      <div style={{marginBottom:20}}>
        <div style={{fontSize:20,fontWeight:700,color:'var(--text)'}}>🤖 Pricing Agent</div>
        <div style={{fontSize:13,color:'var(--text3)',marginTop:4}}>Pick a product. The agent researches the competitor once and returns 3 scored pricing models for every market. Import each market's winner.</div>
      </div>

      <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:18,marginBottom:18,display:'flex',gap:16,flexWrap:'wrap',alignItems:'flex-end'}}>
        <div>
          <label style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.6px',fontFamily:'DM Mono,monospace',display:'block',marginBottom:5}}>Product</label>
          <select value={productId} onChange={e=>setProductId(e.target.value)}
            style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:8,color:'var(--text)',padding:'9px 12px',fontSize:13,minWidth:280,outline:'none'}}>
            <option value="">— select —</option>
            {products.map(p => <option key={p.product_id} value={p.product_id}>{p.product_id} — {p.product_name}</option>)}
          </select>
        </div>
        <button className="btn btn-primary" onClick={run} disabled={running || !productId}>
          {running ? '⏳ Researching all markets… (~2 min)' : '🔍 Analyze All Markets'}
        </button>
        {product && <div style={{fontSize:12,color:'var(--text3)',paddingBottom:10}}>margin {product.profit_margin}% · BE ROAS {product.be_roas}x · {activeMarkets.length} markets</div>}
      </div>

      {running && <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:24,textAlign:'center',color:'var(--text3)'}}>
        <div style={{fontSize:26,marginBottom:8}}>🔎</div>
        <div style={{fontSize:13}}>Researching the competitor and pricing all {activeMarkets.length} markets…</div>
        <div style={{fontSize:11,marginTop:6}}>About 2 minutes. You can leave this open.</div>
      </div>}

      {result && !r && <div style={{background:'var(--bg2)',border:'1px solid rgba(239,68,68,0.35)',borderRadius:12,padding:18}}>
        <div style={{fontSize:13,fontWeight:700,color:'#EF4444',marginBottom:6}}>Couldn't parse a result — try again</div>
        <div style={{fontSize:12,color:'var(--text2)',whiteSpace:'pre-wrap'}}>{(result.raw||'').slice(0,400)}</div>
      </div>}

      {r && <>
        <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:10,padding:'12px 16px',marginBottom:16,fontSize:12}}>
          <span style={{color:'var(--text3)'}}>Competitor: </span>
          <strong style={{color:'var(--text)'}}>{r.competitor?.offer||'—'}</strong>
          <span style={{marginLeft:8,fontSize:10,color:'var(--text3)'}}>({r.competitor?.confidence||'?'} confidence)</span>
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {(r.markets||[]).map(mk => {
            const best = (mk.models||[]).reduce((a,b)=> (b.score>(a?.score??-1)?b:a), null)
            return (
              <div key={mk.market} style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:14}}>
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
                  <MarketBadge market={mk.market} />
                  <span style={{fontSize:11,color:'var(--text3)',fontFamily:'DM Mono,monospace'}}>{mk.currency}</span>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:10}}>
                  {(mk.models||[]).map((model,i) => {
                    const isBest = model === best
                    return (
                      <div key={i} style={{background:'var(--bg3)',border:'1px solid '+(isBest?'var(--accent3)':'var(--border2)'),borderRadius:10,padding:'12px 14px',display:'flex',flexDirection:'column'}}>
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                          <span style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.5px',fontFamily:'DM Mono,monospace'}}>{model.strategy}{isBest?' ★':''}</span>
                          <span style={{fontSize:14,fontWeight:700,color:scoreColor(model.score),fontFamily:'DM Mono,monospace'}}>{model.score}/10</span>
                        </div>
                        <div style={{fontSize:14,fontWeight:700,color:'var(--text)',lineHeight:1.3,marginBottom:6}}>{model.offer}</div>
                        {model.reason && <div style={{fontSize:11,color:'var(--text3)',lineHeight:1.4,marginBottom:10,flex:1}}>{model.reason}</div>}
                        <button className="btn btn-secondary" style={{width:'100%',fontSize:12}} disabled={applying===mk.market+model.strategy} onClick={()=>importModel(mk.market, model)}>
                          {applying===mk.market+model.strategy ? 'Importing…' : `Import to ${mk.market}`}
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
        {result.searches?.length>0 && <div style={{fontSize:10,color:'var(--text3)',marginTop:14,fontFamily:'DM Mono,monospace'}}>Searched: {result.searches.join(' · ')}</div>}
      </>}
    </div>
  )
}
